import { Suspense, lazy } from 'react';

import { RoutePermissionGuard } from '@/components/guards/route-permission-guard';
import AppLayout from '@/components/layouts/app-index';
import { AuthGuard } from '@/utils/auth';

// Lazy-loaded page components for route-based code splitting
const Analytics = lazy(() => import('@/pages/analytics'));
const Customers = lazy(() => import('@/pages/customers'));
const CustomerShow = lazy(() => import('@/pages/customers/show'));
const Dashboard = lazy(() => import('@/pages/dashboard'));
const Inventory = lazy(() => import('@/pages/inventory'));
const InventoryShow = lazy(() => import('@/pages/inventory/show'));
const Notifications = lazy(() => import('@/pages/notifications'));
const Orders = lazy(() => import('@/pages/orders'));
const OrderShow = lazy(() => import('@/pages/orders/show'));
const Promotions = lazy(() => import('@/pages/promo'));
const DiscountShow = lazy(() => import('@/pages/promo/show'));
const PurchaseOrder = lazy(() => import('@/pages/purchase-order'));
const PurchaseOrderShow = lazy(() => import('@/pages/purchase-order/show'));
const Requisition = lazy(() => import('@/pages/purchase-requisition'));
const RequisitionDetails = lazy(
  () => import('@/pages/purchase-requisition/show')
);
const Regions = lazy(() => import('@/pages/regions'));
const RegionShow = lazy(() => import('@/pages/regions/show'));
const Reports = lazy(() => import('@/pages/reports'));
const Riders = lazy(() => import('@/pages/riders'));
const RiderShow = lazy(() => import('@/pages/riders/show'));
const Settings = lazy(() => import('@/pages/settings'));
const Suppliers = lazy(() => import('@/pages/suppliers'));
const SupplierShow = lazy(() => import('@/pages/suppliers/show'));
const SystemLogs = lazy(() => import('@/pages/system-logs'));
const Transactions = lazy(() => import('@/pages/transactions'));
const TransactionsShow = lazy(() => import('@/pages/transactions/show'));
const Users = lazy(() => import('@/pages/users'));

interface RouteConfig {
  path: string;
  title?: string;
}

export interface RouteConfigMap {
  dashboard: RouteConfig;
  orders: RouteConfig;
  inventory: RouteConfig;
  users: RouteConfig;
  customers: RouteConfig;
  suppliers: RouteConfig;
  riders: RouteConfig;
  discount: RouteConfig;
  notifications: RouteConfig;
  settings: RouteConfig;
  requisition: RouteConfig;
  regions: RouteConfig;
  purchase: RouteConfig;
  transactions: RouteConfig;
  analytics: RouteConfig;
  systemLogs: RouteConfig;
  reports: RouteConfig;
  [key: string]: RouteConfig;
}

export const routeConfig: RouteConfigMap = {
  dashboard: {
    path: '',
    title: 'Overview',
  },
  orders: {
    path: 'orders',
    title: 'Orders',
  },
  inventory: {
    path: 'inventory',
    title: 'Inventory',
  },
  users: {
    path: 'users',
    title: 'Users',
  },
  customers: {
    path: 'customers',
    title: 'Customers',
  },
  regions: {
    path: 'regions',
    title: 'Sourcing Regions',
  },
  suppliers: {
    path: 'suppliers',
    title: 'Suppliers',
  },
  riders: {
    path: 'riders',
    title: 'Riders',
  },
  settings: {
    path: 'settings',
    title: 'Settings',
  },
  notifications: {
    path: 'notifications',
    title: 'Notifications',
  },
  purchase: {
    path: 'purchase-orders',
    title: 'Purchase Orders',
  },
  discount: {
    path: 'discount',
    title: 'Discounts',
  },
  transactions: {
    path: 'transactions',
    title: 'Transactions',
  },
  requisition: {
    path: 'requisition',
    title: 'Requisitions',
  },
  analytics: {
    path: 'analytics',
    title: 'Analytics',
  },
  systemLogs: {
    path: 'system-logs',
    title: 'System Logs',
  },
  reports: {
    path: 'reports',
    title: 'Reports',
  },
};

const Fallback = (
  <div className='p-6 text-sm text-muted-foreground'>Loading…</div>
);

// =======|| APP ROUTES (AUTHENTICATED) ||========= //
const AppRoutes = [
  {
    path: '/',
    element: (
      <AuthGuard>
        <RoutePermissionGuard>
          <AppLayout />
        </RoutePermissionGuard>
      </AuthGuard>
    ),
    children: [
      // Dashboard (root)
      {
        index: true,
        element: (
          <Suspense fallback={Fallback}>
            <Dashboard />
          </Suspense>
        ),
      },

      // Core Management Pages
      {
        path: routeConfig.orders.path,
        element: (
          <Suspense fallback={Fallback}>
            <Orders />
          </Suspense>
        ),
      },
      {
        path: routeConfig.inventory.path,
        element: (
          <Suspense fallback={Fallback}>
            <Inventory />
          </Suspense>
        ),
      },
      {
        path: routeConfig.customers.path,
        element: (
          <Suspense fallback={Fallback}>
            <Customers />
          </Suspense>
        ),
      },
      {
        path: routeConfig.suppliers.path,
        element: (
          <Suspense fallback={Fallback}>
            <Suppliers />
          </Suspense>
        ),
      },
      {
        path: routeConfig.riders.path,
        element: (
          <Suspense fallback={Fallback}>
            <Riders />
          </Suspense>
        ),
      },

      // Purchase Management
      {
        path: routeConfig.purchase.path,
        element: (
          <Suspense fallback={Fallback}>
            <PurchaseOrder />
          </Suspense>
        ),
      },
      {
        path: routeConfig.requisition.path,
        element: (
          <Suspense fallback={Fallback}>
            <Requisition />
          </Suspense>
        ),
      },

      // Business Operations
      {
        path: routeConfig.discount.path,
        element: (
          <Suspense fallback={Fallback}>
            <Promotions />
          </Suspense>
        ),
      },
      {
        path: routeConfig.transactions.path,
        element: (
          <Suspense fallback={Fallback}>
            <Transactions />
          </Suspense>
        ),
      },
      {
        path: routeConfig.regions.path,
        element: (
          <Suspense fallback={Fallback}>
            <Regions />
          </Suspense>
        ),
      },

      // Administration
      {
        path: routeConfig.users.path,
        element: (
          <Suspense fallback={Fallback}>
            <Users />
          </Suspense>
        ),
      },
      {
        path: routeConfig.notifications.path,
        element: (
          <Suspense fallback={Fallback}>
            <Notifications />
          </Suspense>
        ),
      },
      {
        path: routeConfig.settings.path,
        element: (
          <Suspense fallback={Fallback}>
            <Settings />
          </Suspense>
        ),
      },
      {
        path: routeConfig.systemLogs.path,
        element: (
          <Suspense fallback={Fallback}>
            <SystemLogs />
          </Suspense>
        ),
      },

      // Analytics & Reporting
      {
        path: routeConfig.analytics.path,
        element: (
          <Suspense fallback={Fallback}>
            <Analytics />
          </Suspense>
        ),
      },

      // Detail Pages - Core Management
      {
        path: 'customers/:id',
        element: (
          <Suspense fallback={Fallback}>
            <CustomerShow />
          </Suspense>
        ),
      },
      {
        path: 'inventory/:id',
        element: (
          <Suspense fallback={Fallback}>
            <InventoryShow />
          </Suspense>
        ),
      },
      {
        path: 'suppliers/:id',
        element: (
          <Suspense fallback={Fallback}>
            <SupplierShow />
          </Suspense>
        ),
      },
      {
        path: 'riders/:id',
        element: (
          <Suspense fallback={Fallback}>
            <RiderShow />
          </Suspense>
        ),
      },
      {
        path: 'orders/:id',
        // element: <OrderDetails />,
        element: (
          <Suspense fallback={Fallback}>
            <OrderShow />
          </Suspense>
        ),
      },

      // Detail Pages - Purchase Management
      {
        path: 'purchase-orders/:id',
        element: (
          <Suspense fallback={Fallback}>
            <PurchaseOrderShow />
          </Suspense>
        ),
      },
      {
        path: 'requisition/:id',
        element: (
          <Suspense fallback={Fallback}>
            <RequisitionDetails />
          </Suspense>
        ),
      },

      // Detail Pages - Business Operations
      {
        path: 'discount/:id',
        element: (
          <Suspense fallback={Fallback}>
            <DiscountShow />
          </Suspense>
        ),
      },
      {
        path: 'transactions/:id',
        element: (
          <Suspense fallback={Fallback}>
            <TransactionsShow />
          </Suspense>
        ),
      },
      {
        path: 'regions/:id',
        element: (
          <Suspense fallback={Fallback}>
            <RegionShow />
          </Suspense>
        ),
      },
      {
        path: routeConfig.reports.path,
        element: (
          <Suspense fallback={Fallback}>
            <Reports />
          </Suspense>
        ),
      },
    ],
  },
];

export default AppRoutes;
